import React from 'react';
import { Link } from 'react-router-dom';
import { User, Mail, Phone, Lock, Dumbbell, Search, MapPin, CreditCard } from 'lucide-react';
import BLKLogo from '../components/BLKLogo';

const SUPPORT_EMAIL = 'admin@blkpixeltech.com';

const accounts = [
  {
    role: 'Trainee (client)',
    icon: Search,
    name: 'Apple Trainee',
    email: 'appletester@test.com',
    phone: '3013013010',
    password: 'appletest',
    testid: 'trainee',
  },
  {
    role: 'Trainer (coach)',
    icon: Dumbbell,
    name: 'Apple Test Trainer',
    email: 'appletrainer@test.com',
    phone: '3013013010',
    password: 'appletest',
    testid: 'trainer',
  },
];

const traineeSteps = [
  'Open the app and sign in with the Trainee credentials above.',
  'On the welcome screen, tap “Find a Trainer.”',
  'When prompted, allow location “While Using the App” — this is used to surface nearby trainers and estimate arrival times.',
  'Browse the list/map of nearby trainers and open a trainer profile to view bio, specialties, certifications, pricing, and highlight reel.',
  'Request/book a session with a trainer and choose a time.',
  'Complete payment on the checkout screen using a Stripe test card (e.g. 4242 4242 4242 4242, any future expiry, any CVC, any ZIP).',
  'After the trainer accepts, view the live session screen (ETA and en-route tracking).',
  'Open the in-app chat and send a message to the trainer.',
  'After a completed session, leave a review and optionally add a tip.',
  'Test account deletion: Settings → Account → Delete Account (this permanently removes the account and data).',
];

const trainerSteps = [
  'Sign out, then sign in with the Trainer credentials above (this account is already approved).',
  'Review the “Become a Trainer” / profile setup flow: specialties, goals, certifications, pricing, highlight reel videos, and payout handle (Zelle/PayPal/Venmo/CashApp).',
  'Allow location permission so you can share live progress during an active session.',
  'When the Trainee books, accept the incoming booking request (push notification deep-links to the request).',
  'Start the session and set status to “en-route” so the trainee can see your live location and ETA.',
  'Use in-app chat to message the trainee.',
  'Mark the session complete and confirm it appears in session history/earnings.',
  'Review notification preferences to enable/disable push notifications.',
];

const Credential = ({ icon: Icon, label, value, mono }) => (
  <div className="flex items-center gap-3 py-2">
    <Icon className="w-4 h-4 text-[#c084fc] flex-shrink-0" />
    <span className="text-gray-400 text-[12px] w-[72px] flex-shrink-0">{label}</span>
    <span className={`text-white text-[13.5px] ${mono ? 'font-mono' : ''} break-all`}>{value}</span>
  </div>
);

const Steps = ({ items, testid }) => (
  <ol className="mt-4 space-y-3" data-testid={`${testid}-steps`}>
    {items.map((s, i) => (
      <li key={i} className="flex gap-3">
        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-[#7c3aed] to-[#4f46e5] text-white text-[12px] font-semibold flex items-center justify-center">
          {i + 1}
        </span>
        <span className="text-gray-300 text-[14px] leading-relaxed pt-0.5">{s}</span>
      </li>
    ))}
  </ol>
);

export default function RapidRepsReview() {
  return (
    <div className="relative min-h-screen" data-testid="rapidreps-review-page">
      <header className="border-b border-white/5 bg-[#05050a]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[920px] mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="review-home-link">
            <BLKLogo />
          </Link>
          <Link
            to="/rapidreps-support"
            className="text-[13px] text-gray-300 hover:text-white transition-colors"
            data-testid="review-support-link"
          >
            ← Support
          </Link>
        </div>
      </header>

      <main className="max-w-[920px] mx-auto px-6 lg:px-8 py-14">
        <div className="text-[12px] font-semibold tracking-[0.3em] mb-4">
          <span className="bg-gradient-to-r from-[#a855f7] to-[#60a5fa] bg-clip-text text-transparent">
            RAPIDREPS — APP REVIEW
          </span>
        </div>
        <h1 className="text-white text-4xl sm:text-5xl font-bold tracking-tight">Test Instructions</h1>
        <p className="text-gray-400 text-[15px] leading-relaxed mt-5 max-w-[680px]">
          RapidReps connects trainees with nearby personal trainers for on-demand sessions. Below are two
          demo accounts and the key flows to review. Both roles can be fully exercised with the credentials
          provided. RapidReps is intended for users 18 or older.
        </p>

        {/* Credentials */}
        <div className="grid sm:grid-cols-2 gap-4 mt-10">
          {accounts.map((a) => (
            <div
              key={a.testid}
              data-testid={`cred-${a.testid}`}
              className="rounded-2xl p-6 bg-gradient-to-b from-[#1a0f2e]/60 to-[#0f0817]/60 border border-white/10"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#7c3aed]/30 to-[#4f46e5]/20 border border-[#a855f7]/30 flex items-center justify-center">
                  <a.icon className="w-4 h-4 text-[#c084fc]" />
                </div>
                <div className="text-white font-semibold text-[15px]">{a.role}</div>
              </div>
              <div className="mt-4 divide-y divide-white/5">
                <Credential icon={User} label="Name" value={a.name} />
                <Credential icon={Mail} label="Email" value={a.email} mono />
                <Credential icon={Phone} label="Phone" value={a.phone} mono />
                <Credential icon={Lock} label="Password" value={a.password} mono />
              </div>
            </div>
          ))}
        </div>

        {/* Trainee flow */}
        <section className="mt-14">
          <div className="flex items-center gap-2.5">
            <Search className="w-5 h-5 text-[#c084fc]" />
            <h2 className="text-white text-xl font-bold tracking-tight">Reviewing as a Trainee</h2>
          </div>
          <p className="text-gray-400 text-[13px] mt-1">Finding and booking a trainer, paying, and tracking a session.</p>
          <Steps items={traineeSteps} testid="trainee" />
        </section>

        {/* Trainer flow */}
        <section className="mt-12">
          <div className="flex items-center gap-2.5">
            <Dumbbell className="w-5 h-5 text-[#c084fc]" />
            <h2 className="text-white text-xl font-bold tracking-tight">Reviewing as a Trainer</h2>
          </div>
          <p className="text-gray-400 text-[13px] mt-1">Setting up a profile, accepting bookings, and running a live session.</p>
          <Steps items={trainerSteps} testid="trainer" />
        </section>

        {/* Reviewer notes */}
        <section className="mt-12 rounded-2xl p-6 bg-[#0d0d16]/70 border border-white/5">
          <h2 className="text-white text-lg font-semibold">Notes for the reviewer</h2>
          <ul className="mt-4 space-y-3 text-gray-300 text-[14px] leading-relaxed">
            <li className="flex gap-2.5">
              <MapPin className="w-4 h-4 text-[#c084fc] mt-0.5 flex-shrink-0" />
              <span><strong className="text-white">Location:</strong> matching, ETAs, and live tracking require location access. “While Using” is enough to test core flows; “Always” enables en-route background tracking, which stops automatically when a session ends.</span>
            </li>
            <li className="flex gap-2.5">
              <CreditCard className="w-4 h-4 text-[#c084fc] mt-0.5 flex-shrink-0" />
              <span><strong className="text-white">Payments:</strong> processed via Stripe in test mode. Use test card <span className="font-mono">4242 4242 4242 4242</span>, any future expiry, any CVC. No real charges occur.</span>
            </li>
            <li className="flex gap-2.5">
              <Mail className="w-4 h-4 text-[#c084fc] mt-0.5 flex-shrink-0" />
              <span><strong className="text-white">Support:</strong> questions during review can be sent to <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#c084fc] hover:underline">{SUPPORT_EMAIL}</a>. We respond within 1–2 business days.</span>
            </li>
          </ul>
          <div className="mt-5 flex flex-wrap gap-4 text-[13px]">
            <Link to="/rapidreps-support" className="text-[#c084fc] hover:underline" data-testid="review-to-support">Support page →</Link>
            <Link to="/rapidreps-privacy" className="text-[#c084fc] hover:underline" data-testid="review-to-privacy">Privacy policy →</Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/5 mt-10">
        <div className="max-w-[920px] mx-auto px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-gray-500 text-[12px]">
            © {new Date().getFullYear()} BLKPixel Technologies, LLC. All rights reserved.
          </div>
          <div className="flex items-center gap-5 text-[12px]">
            <Link to="/rapidreps-support" className="text-gray-400 hover:text-white">Support</Link>
            <Link to="/rapidreps-privacy" className="text-gray-400 hover:text-white">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
